import React from "react";

import {
  KintoneFormField,
  KintoneFormFields,
  MetadataFieldMappingValidationError,
  nullToUndefinedDeep,
} from "@graffer-inc/cloud-gov-core";
import { TextField } from "@graffer-inc/console-design-kit";
import { UpdateKintoneExportSettingInput } from "@graffer-inc/smart-apply";

import {
  fieldMappingErrorCodeToMessage,
  validateMetadataFieldMapping,
} from "../../../../../../../application/domain/kintone-export";
import { useKintoneFieldCandidates } from "./hook";
import { KintoneFieldSelect } from "./kintone-field-select";

type MetadataFieldMappingFormProps = {
  metadataFieldMapping: UpdateKintoneExportSettingInput["metadataFieldMapping"];
  onChange: (
    metadataFieldMapping: UpdateKintoneExportSettingInput["metadataFieldMapping"],
  ) => void;
  kintoneFormFields?: KintoneFormFields;

  setUsedKintoneFields: (v: Set<KintoneFormField["code"]>) => void;

  shouldDisplayError?: boolean;
};

export const MetadataFieldMappingForm: React.FC<
  MetadataFieldMappingFormProps
> = ({
  metadataFieldMapping,
  onChange,
  kintoneFormFields,
  setUsedKintoneFields,
  shouldDisplayError,
}) => {
  const [validationErrors, setValidationErrors] = React.useState<
    MetadataFieldMappingValidationError[]
  >([]);
  React.useEffect(() => {
    const { errors, usedKintoneFieldCode } = validateMetadataFieldMapping({
      value: nullToUndefinedDeep(metadataFieldMapping),
      kintoneFields: kintoneFormFields,
    });
    setValidationErrors(errors);
    setUsedKintoneFields(usedKintoneFieldCode);
  }, [
    metadataFieldMapping,
    kintoneFormFields,
    setUsedKintoneFields,
    setValidationErrors,
  ]);
  const errorCodeForAppId = validationErrors.find(
    ({ mappingKey }) => mappingKey === "submissionIdFieldCode",
  )?.errorCode;
  const errorCodeForAppliedAt = validationErrors.find(
    ({ mappingKey }) => mappingKey === "submittedAtFieldCode",
  )?.errorCode;
  const errorCodeForApplicationStatus = validationErrors.find(
    ({ mappingKey }) => mappingKey === "submissionStatusFieldCode",
  )?.errorCode;
  const errorCodeForProcedureName = validationErrors.find(
    ({ mappingKey }) => mappingKey === "procedureNameFieldCode",
  )?.errorCode;

  const { filteredKintoneFields } = useKintoneFieldCandidates({
    kintoneFormFields,
  });

  return (
    <div className="columns">
      <div className="column">
        <div className="field">
          <label className="label">メタデータのマッピング設定</label>
          {filteredKintoneFields ? (
            <KintoneFieldSelect
              value={metadataFieldMapping.submissionIdFieldCode}
              onChange={(kintoneFieldCode) => {
                onChange({
                  ...metadataFieldMapping,
                  submissionIdFieldCode: kintoneFieldCode,
                });
              }}
              kintoneFieldsMap={filteredKintoneFields}
              label="申請 ID をマッピングする kintone フィールド"
              description="一行テキストのフィールドを指定してください。"
              errorMessages={
                errorCodeForAppId
                  ? fieldMappingErrorCodeToMessage(errorCodeForAppId)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          ) : (
            <TextField
              label="申請 ID をマッピングする kintone フィールドコード"
              value={metadataFieldMapping.submissionIdFieldCode}
              onChange={(e) => {
                onChange({
                  ...metadataFieldMapping,
                  submissionIdFieldCode: e.target.value,
                });
              }}
              required
              placeholder=""
              description="一行テキストのフィールドを指定してください。"
              errorMessages={
                errorCodeForAppId
                  ? fieldMappingErrorCodeToMessage(errorCodeForAppId)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          )}

          <div className="mb-2" />

          {filteredKintoneFields ? (
            <KintoneFieldSelect
              value={metadataFieldMapping.submittedAtFieldCode}
              onChange={(kintoneFieldCode) => {
                onChange({
                  ...metadataFieldMapping,
                  submittedAtFieldCode: kintoneFieldCode,
                });
              }}
              kintoneFieldsMap={filteredKintoneFields}
              label="申請日をマッピングする kintone フィールド"
              description="日付、または日時のフィールドを指定してください。"
              errorMessages={
                errorCodeForAppliedAt
                  ? fieldMappingErrorCodeToMessage(errorCodeForAppliedAt)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          ) : (
            <TextField
              label="申請日をマッピングする kintone フィールドコード"
              value={metadataFieldMapping.submittedAtFieldCode}
              onChange={(e) => {
                onChange({
                  ...metadataFieldMapping,
                  submittedAtFieldCode: e.target.value,
                });
              }}
              required
              placeholder=""
              description="日付、または日時のフィールドを指定してください。"
              errorMessages={
                errorCodeForAppliedAt
                  ? fieldMappingErrorCodeToMessage(errorCodeForAppliedAt)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          )}

          <div className="mb-2" />

          {filteredKintoneFields ? (
            <KintoneFieldSelect
              value={metadataFieldMapping.submissionStatusFieldCode ?? ""}
              onChange={(kintoneFieldCode) => {
                onChange({
                  ...metadataFieldMapping,
                  submissionStatusFieldCode: kintoneFieldCode
                    ? kintoneFieldCode
                    : undefined,
                });
              }}
              kintoneFieldsMap={filteredKintoneFields}
              label="申請ステータスをマッピングする kintone フィールド"
              description="「未処理」「処理中」「完了」「差し戻し」「取下げ」の選択肢を持つドロップダウンを指定してください。"
              errorMessages={
                errorCodeForApplicationStatus
                  ? fieldMappingErrorCodeToMessage(
                      errorCodeForApplicationStatus,
                    )
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          ) : (
            <TextField
              label="申請ステータスをマッピングする kintone フィールドコード"
              value={metadataFieldMapping.submissionStatusFieldCode ?? ""}
              onChange={(e) => {
                onChange({
                  ...metadataFieldMapping,
                  submissionStatusFieldCode: e.target.value
                    ? e.target.value
                    : undefined,
                });
              }}
              required
              placeholder=""
              description="「未処理」「処理中」「完了」「差し戻し」「取下げ」の選択肢を持つドロップダウンを指定してください。"
              errorMessages={
                errorCodeForApplicationStatus
                  ? fieldMappingErrorCodeToMessage(
                      errorCodeForApplicationStatus,
                    )
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          )}

          <div className="mb-2" />

          {filteredKintoneFields ? (
            <KintoneFieldSelect
              value={metadataFieldMapping.procedureNameFieldCode ?? ""}
              onChange={(kintoneFieldCode) => {
                onChange({
                  ...metadataFieldMapping,
                  procedureNameFieldCode: kintoneFieldCode
                    ? kintoneFieldCode
                    : undefined,
                });
              }}
              kintoneFieldsMap={filteredKintoneFields}
              label="手続き名称を連携する kintone フィールド"
              description="一行テキストのフィールドを指定してください。"
              errorMessages={
                errorCodeForProcedureName
                  ? fieldMappingErrorCodeToMessage(errorCodeForProcedureName)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          ) : (
            <TextField
              label="手続き名称を連携する kintone フィールド"
              value={metadataFieldMapping.procedureNameFieldCode ?? ""}
              onChange={(e) => {
                onChange({
                  ...metadataFieldMapping,
                  procedureNameFieldCode: e.target.value
                    ? e.target.value
                    : undefined,
                });
              }}
              required
              placeholder=""
              description="一行テキストのフィールドを指定してください。"
              errorMessages={
                errorCodeForProcedureName
                  ? fieldMappingErrorCodeToMessage(errorCodeForProcedureName)
                  : undefined
              }
              shouldDisplayError={shouldDisplayError}
            />
          )}
        </div>
      </div>
    </div>
  );
};
